import itiriri from 'itiriri';
import React, { useMemo, useState } from "react";
import { useHistory, useParams } from 'react-router-dom';
import { WardDto } from "server-openapi";
import styled from 'styled-components';
import { IconBack } from '../../kit/Icons/Back';
import { Layout } from '../../kit/Layout';
import { Text } from '../../kit/Text';
import { useSyncCenter } from '../../syncstream/SyncCenterProvider';
import { useStore } from '../../core/storage/hooks/UseStore';
import { DashboardNavbar } from './DashboardPage/components/DashboardNavbar';
import { Grid } from '../../kit/Grid';
import { SearchInput } from '../../kit/Forms/SearchInput';
import { Select } from '../../kit/Forms/Select';
import { DateUtils , Interval} from '../../core/utils/dateUtils';
import { useApiUtils } from '../../syncstream/utils/hooks/useApiUtils';
import { endOfDay, startOfDay } from "date-fns";
import { RoundScheduleItem } from '../../syncstream/utils/RoundUtils';
import { TimeCriticalDosesCard } from "./components/TimeCriticalDosesCard";
import { groupBy } from "lodash-es";
import Badge from "../../kit/Badge";

interface IParams {
    facilityGroupId: string;
}

const useTimeCriticalDosesData = (facilityGroupId: string) => {
    const services = useSyncCenter();
    const apiUtils = useApiUtils();

    // Stores
    const facilityGroupsStore = useStore(services.facilityGroups.store).store;
    const facilityStore = useStore(services.facilities.store).store;
    const drugStore = useStore(services.drugs.store).store;

    const facilityGroup = itiriri(facilityGroupsStore.values())
        .find((f) => f.hsId?.toString() === facilityGroupId);

    const facilities = itiriri(facilityStore.values())
        .filter((facility) => facility.facilityGroupId?.toString() === facilityGroupId)
        .toArray();

    const windowForToday: Interval = {
        start: DateUtils.fromDate(startOfDay(new Date())),
        end: DateUtils.fromDate(endOfDay(new Date()))
    };

    // Get schedule items
    const facilityIds = facilities.map((x) => x.hsId!);
    const scheduleItemsForToday = apiUtils.rounds.getScheduleFromParameters(windowForToday, facilityIds, true);
    const timeCriticalScheduleItems = apiUtils.rounds.getTimeCriticalScheduleItemsForRounds(scheduleItemsForToday);

    return {
        facilityGroup,
        facilities,
        drugStore,
        facilityStore,
        timeCriticalScheduleItems,
    };
};

const useFilteredScheduleItems = (
    timeCriticalScheduleItems: RoundScheduleItem[],
    patientUtils: ReturnType<typeof useApiUtils>['patients'],
    filterWing?: WardDto,
    filterPatientName = ''

) => {
    return useMemo(() => {
        const filteredPatients = timeCriticalScheduleItems.filter((item) => {
            if (filterWing && item.patient.facility !== filterWing.hsId) {
                return false;
            }
            if (filterPatientName) {
                const residentName = patientUtils.getQueryPatientString(item.patient);
                return residentName.includes(filterPatientName.toUpperCase());
            }
            return true;
        });

        return groupBy(filteredPatients, (i) =>
            patientUtils.getDisplayPatientName(i.patient));
    }, [filterWing, filterPatientName, timeCriticalScheduleItems, patientUtils]);
};

export function TimeCriticalDosesPage() {
    // Get the currently selected facility from the URL parameters.
    const { facilityGroupId } = useParams<IParams>();
    const history = useHistory();
    const apiUtils = useApiUtils();
    const patientUtils = apiUtils.patients;

    // State
    const [filterPatientName, setFilterPatientName] = useState<string>('');
    const [filterWing, setFilterWing] = useState<WardDto>();

    // Custom hooks
    const {
        facilityGroup,
        facilities,
        drugStore,
        facilityStore,
        timeCriticalScheduleItems,
    } = useTimeCriticalDosesData(facilityGroupId);

    const filteredScheduleItems = useFilteredScheduleItems(
        timeCriticalScheduleItems,
        patientUtils, // Pass patientUtils as a parameter
        filterWing,
        filterPatientName
    );

    // Derived data
    const facilityOptions = facilities.map((facility) => ({
        label: facility.name ?? 'Unknown',
        value: facility,
    }));

    const wingLabel = apiUtils.facilityGroups.getFacilityUILabel(parseInt(facilityGroupId));
    const residentLabel = apiUtils.facilityGroups.getResidentUILabel(parseInt(facilityGroupId));

    return (
        <Layout>
            <DashboardNavbar facilityGroup={facilityGroup} />
            <Container gap={1}>
                <Layout horizontal gap={1.5}>
                    <IconBack onClick={() => history.push(`/facility-group/${facilityGroupId}`)} />
                    <Layout horizontal>
                        <Text weight={'bold'} size="large" style={{color: 'white'}}>
                            Time Sensitive Doses
                        </Text>
                        <Badge type={'info'} text={Object.values(filteredScheduleItems).flat().length} />
                    </Layout>
                </Layout>
                <Grid cols={2} colsMobile={1} gap={1}>
                    <SearchInput
                        onChange={(_, v) => setFilterPatientName(v)}
                        value={filterPatientName}
                        name="search"
                        placeholder={`Search ${residentLabel}`}
                        fullWidth
                    />
                    {facilityOptions.length === 1 ? (
                        <WingNameBox>
                            <Text>{facilityOptions[0].label}</Text>
                        </WingNameBox>
                    ) : (
                        <Select
                            fullWidth
                            placeholder={`All ${wingLabel}`}
                            name="wing"
                            options={facilityOptions}
                            value={filterWing}
                            onChange={(_, value) => setFilterWing(value)}
                        />
                    )}
                </Grid>
                {Object.values(filteredScheduleItems)
                    .flatMap(items => items)
                    .sort((a, b) => {
                        // First compare by timestamp
                        const timeCompare = (a.packedMedication?.doseTimestamp || '').localeCompare(b.packedMedication?.doseTimestamp || '');
                        if (timeCompare !== 0) return timeCompare;

                        // If timestamps are equal, compare by patient name
                        return ((a.patient.familyName ?? '') + (a.patient.givenName ?? ''))
                            .localeCompare((b.patient.familyName ?? '') + (b.patient.givenName ?? ''));
                    })
                    .map((scheduleItem: RoundScheduleItem, index) => (
                        <TimeCriticalDosesCard
                            key={index}
                            scheduleItem={scheduleItem}
                            facilityGroup={facilityGroup!}
                            facilityStore={facilityStore}
                            drugStore={drugStore}
                        />
                    ))
                }
            </Container>
        </Layout>
    );
}

const Container = styled(Layout)`
    margin: 1.5rem;
`;

const WingNameBox = styled(Layout)`
    display: flex;
    padding: ${(props) => props.theme.forms.input.padding};
    align-items: center;
    background: white;
`;
